import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, Book } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ResourceDownloadModal from '../components/ResourceDownloadModal';

export const Resources = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);

  const handleDownloadClick = (resource) => {
    setSelectedResource(resource);
    setIsModalOpen(true);
  };

  const resources = {
    guides: [
      {
        slug: "brand-strategy-canvas-guide",
        title: "The Guide to Using the Brand Strategy Canvas",
        description: "A step by step guide to applying the Brand Strategy Canvas to your business.",
        type: "PDF Guide",
        icon: FileText
      },
      {
        slug: "genai-marketers-playbook",
        title: "GenAI for Marketers: A Practical Playbook",
        description: "Real workflows, tools, and use cases for AI-powered content creation.",
        type: "PDF Guide",
        icon: FileText
      },
      {
        slug: "growth-marketing-measurement-framework",
        title: "Growth Marketing Measurement Framework",
        description: "How to connect metrics across the funnel for true ROI visibility.",
        type: "PDF Guide",
        icon: FileText
      }
    ],
    templates: [
      {
        slug: "brand-strategy-canvas-template",
        title: "Brand Strategy Canvas",
        description: "Our internal template for mapping business goals to brand expression.",
        type: "PDF Guide",
        icon: FileText
      },
      {
        slug: "campaign-planning-worksheet",
        title: "Campaign Planning Worksheet",
        description: "End-to-end planning framework from insight to execution.",
        type: "Google Sheets",
        icon: FileText
      },
      {
        slug: "marketing-performance-dashboard",
        title: "Marketing Performance Dashboard",
        description: "Track the metrics that matter across channels and campaigns.",
        type: "Excel Template",
        icon: FileText
      }
    ]
  };

  const ResourceCard = ({ resource }) => {
    const Icon = resource.icon;
    return (
      <Card className="resource-card group hover-lift">
        <CardContent className="p-8">
          <div className="flex items-start justify-between mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-red-600/20 to-red-700/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Icon className="w-7 h-7 text-red-500" />
            </div>
            <span className="text-xs text-gray-500 uppercase tracking-wider">{resource.type}</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gradient transition-all">
            {resource.title}
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            {resource.description}
          </p>
          <Button 
            variant="outline" 
            className="btn-secondary group w-full"
            onClick={() => handleDownloadClick(resource)}
          >
            <Download className="mr-2 w-4 h-4 group-hover:translate-y-1 transition-transform" />
            Download Resource
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="resources-page pt-20">
      <ResourceDownloadModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        resource={selectedResource}
      />
      {/* Hero Section */}
      <section className="resources-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              Resources & Tools:<br />
              <span className="text-gradient">Knowledge You Can Use</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Free guides, templates, and frameworks to help you build better brands and drive growth.
            </p>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="resources-grid py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <Tabs defaultValue="guides" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-16 bg-black border border-gray-800">
              <TabsTrigger value="guides" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                Guides
              </TabsTrigger>
              <TabsTrigger value="templates" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                Templates
              </TabsTrigger>
            </TabsList>

            <TabsContent value="guides">
              <div className="grid md:grid-cols-3 gap-8">
                {resources.guides.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="templates">
              <div className="grid md:grid-cols-3 gap-8">
                {resources.templates.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Legal Documents */}
      <section className="legal-section py-20 bg-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Legal & Policies</h2>
            <p className="text-gray-400">Transparency in everything we do</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="legal-card hover-lift">
              <CardContent className="p-6 text-center">
                <Book className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Privacy Policy</h3>
                <p className="text-gray-500 text-sm mb-4">How we protect your data</p>
                <Link to="/privacy-policy" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  <Button variant="ghost" className="text-red-500 hover:text-red-400" data-testid="resources-read-privacy-policy">
                    Read Policy
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="legal-card hover-lift">
              <CardContent className="p-6 text-center">
                <Book className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Terms of Service</h3>
                <p className="text-gray-500 text-sm mb-4">Our working agreement</p>
                <Link to="/terms-of-service" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  <Button variant="ghost" className="text-red-500 hover:text-red-400" data-testid="resources-read-terms-of-service">
                    Read Terms
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="legal-card hover-lift">
              <CardContent className="p-6 text-center">
                <Book className="w-8 h-8 text-red-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Cookie Policy</h3>
                <p className="text-gray-500 text-sm mb-4">How we use cookies</p>
                <Link to="/cookie-policy" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  <Button variant="ghost" className="text-red-500 hover:text-red-400" data-testid="resources-read-cookie-policy">
                    Read Policy
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Legal hub link */}
          <div className="text-center mt-10">
            <Link to="/legal" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-gray-400 hover:text-red-500 transition-colors text-sm" data-testid="resources-view-all-legal">
              View all Legal &amp; Policies →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
